import axios from 'axios';
import { storage } from './storage.js';

const apiKey = process.env.VUE_APP_TAVUS_API_KEY;
const replicaId = process.env.VUE_APP_TAVUS_REPLICA_ID;
const personaId = process.env.VUE_APP_TAVUS_PERSONA_ID;
const memoryDetailKey = 'govava_chat_memory_detail';
const memoryOccasionKey = 'govava_chat_memory_occasion';
const preferenceStorageKey = 'govava_chat_preferences';

const normalizeMemoryText = (text) => {
  if (!text) return null;
  const cleaned = String(text).replace(/\s+/g, ' ').trim();
  if (!cleaned) return null;
  return cleaned.length > 160 ? `${cleaned.slice(0, 157)}...` : cleaned;
};

const readStoredPreferences = () => {
  try {
    const stored = localStorage.getItem(preferenceStorageKey);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (err) {
    console.warn('Failed to read stored preferences:', err);
    return null;
  }
};

const buildPreferenceSummary = () => {
  const stored = readStoredPreferences();
  if (!stored) return null;
  const persona = normalizeMemoryText(stored.persona);
  const activities = normalizeMemoryText(stored.activities);
  if (persona && activities) {
    return `Last time you chose: ${persona}. You also said they enjoy ${activities}.`;
  }
  if (persona) {
    return `Last time you chose: ${persona}.`;
  }
  if (activities) {
    return `Last time you said they enjoy ${activities}.`;
  }
  return null;
};

// Tavus API functions
export const tavusAPI = {
  async createConversation() {
    // Get logged-in user ID from localStorage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData?.user?.id ?? 'guest';
    const memoryStoreDetail = (() => {
      try {
        return localStorage.getItem(memoryDetailKey);
      } catch (err) {
        console.warn('Failed to read memory reminder for greeting:', err);
        return null;
      }
    })();
    const memoryOccasion = (() => {
      try {
        return localStorage.getItem(memoryOccasionKey);
      } catch (err) {
        console.warn('Failed to read memory occasion for greeting:', err);
        return null;
      }
    })();

    const preferenceSummary = buildPreferenceSummary();
    const greetingIntro = memoryStoreDetail ? `${memoryStoreDetail} ` : '';
    const normalizedOccasion = normalizeMemoryText(memoryOccasion);
    const occasionText = normalizedOccasion ? `${normalizedOccasion} ` : '';
    const preferenceText = preferenceSummary ? `${preferenceSummary} ` : '';
    const customGreeting = greetingIntro || occasionText || preferenceText
      ? `Welcome back! ${greetingIntro}${occasionText}${preferenceText}What would you like to search for today?`
      : 'Hi! I m your Govava gift assistant. Who are we shopping for today?';

    const conversationPayload = {
      replica_id: replicaId,
      conversation_name: 'Govava AI Gift Conversation',
      properties: {
        max_call_duration: 1800,
        participant_left_timeout: 60,
        participant_absent_timeout: 300,
        enable_recording: false,
        language: 'english',
     
      },
    };
    
      if (userId) {
        conversationPayload.memory_stores = [`user_${userId}_latest`];
        conversationPayload.custom_greeting = customGreeting;
      }
    // Only add persona_id if it's set
      if (personaId) {
        conversationPayload.persona_id = personaId;
      }
    
    try {
      
      const response = await axios.post('https://tavusapi.com/v2/conversations', conversationPayload, { 
        headers: { 
          'x-api-key': apiKey, 
          'Content-Type': 'application/json' 
        } 
      });
      return response;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  async checkConversationStatus(conversationId) {
    try {
      const response = await axios.get(`https://tavusapi.com/v2/conversations/${conversationId}`, { 
        headers: { 'x-api-key': apiKey } 
      });
      return response.data.status !== 'ended' && response.data.status !== 'failed';
    } catch (error) {
      return false;
    }
  },

  async getConversationDetails(conversationId) {
    try {
      const response = await axios.get(`https://tavusapi.com/v2/conversations/${conversationId}`, { 
        headers: { 'x-api-key': apiKey } 
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get conversation details:', error);
      throw error;
    }
  },

  async endConversation(conversationId) {
    try {
      await axios.post(`https://tavusapi.com/v2/conversations/${conversationId}/end`, {}, { 
        headers: { 'x-api-key': apiKey } 
      });
      storage.setLastConversationEndTime();
    } catch (error) {
      storage.setLastConversationEndTime();
      console.error('Failed to end conversation:', error);
      throw error;
    }
  },

  async getConversationTranscript(conversationId) {
    try {
      const response = await axios.get(`https://tavusapi.com/v2/conversations/${conversationId}?verbose=true`, { 
        headers: { 'x-api-key': apiKey } 
      });
      
      if (response.data.events && response.data.events.length > 0) {
        for (const event of response.data.events) {
          if (event.event_type === 'application.transcription_ready' && event.properties?.transcript) {
            return event.properties.transcript;
          }
        }
      }
      
      if (response.data.transcript) {
        return response.data.transcript;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get conversation transcript:', error);
      return null;
    }
  }
};

// Gift search — same endpoint and payload the website's search bar uses.
export const giftAPI = {
  async searchGifts(searchText, page = 1, resultCursor = null) {
    const keyword = (searchText || '').trim().toLowerCase();
    if (!keyword) throw new Error('Empty search text provided');
    const url = (process.env.VUE_APP_URL || 'https://web-api.govava.com/') + 'customer/search/keyword_search';
    const { data } = await axios.post(url, {
      keyword,
      type: 'keyword',
      category: '',
      page,
      result_cursor: resultCursor,
    }, {
      headers: { 'Content-Type': 'application/json' },
    });
    return data;
  },
}; 

const orderHistoryEndpoint =
  process.env.VUE_APP_ORDER_HISTORY_ENDPOINT ||
  `${process.env.VUE_APP_URL}customer/orders`;

const extractOrders = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload;
  }

  const candidates = ['orders', 'order_history', 'history', 'data', 'records'];
  for (const key of candidates) {
    const value = payload[key];
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
    if (typeof value === 'object' && value !== null) {
      const nested = extractOrders(value);
      if (nested.length > 0) {
        return nested;
      }
    }
  }

  return [];
};

const getOrderItems = (order) => {
  if (!order || typeof order !== 'object') return [];
  const keys = ['items', 'order_items', 'products', 'order_products', 'cart_items', 'cart'];
  for (const key of keys) {
    const candidate = order[key];
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  if (Array.isArray(order?.items_list)) {
    return order.items_list;
  }

  return [];
};

const normalizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/\s+/g, ' ').trim();
};

const findRecipientInfo = (order) => {
  if (!order || typeof order !== 'object') return null;
  const relationKeywords = [
    'mother',
    'father',
    'mom',
    'dad',
    'sister',
    'brother',
    'wife',
    'husband',
    'son',
    'daughter',
    'friend',
    'partner',
    'spouse',
    'aunt',
    'uncle',
    'grandma',
    'grandpa',
    'grandmother',
    'grandfather',
  ];

  const keys = [
    'recipient_relation',
    'relation',
    'gift_for',
    'for_whom',
    'relationship',
    'recipient_name',
    'shipping_name',
    'billing_name',
    'name',
  ];

  for (const key of keys) {
    const value = normalizeText(order[key]);
    if (!value) {
      continue;
    }

    const lowerValue = value.toLowerCase();
    const relation = relationKeywords.find((keyword) => lowerValue.includes(keyword));
    if (relation) {
      return { label: relation, isRelation: true };
    }

    return { label: value, isRelation: false };
  }

  if (order?.occasion) {
    const value = normalizeText(order.occasion?.name ?? order.occasion);
    if (value) {
      return { label: value, isRelation: false };
    }
  }

  return null;
};

const buildOrderSummary = (order) => {
  if (!order) return null;

  const items = getOrderItems(order);
  const firstItem = Array.isArray(items) && items.length > 0 ? items[0] : null;

  const itemNameSources = [
    firstItem?.name,
    firstItem?.title,
    firstItem?.product_name,
    firstItem?.product?.name,
    firstItem?.product?.title,
    firstItem?.productname,
    order?.product_name,
    order?.item_name,
  ];

  const itemName = normalizeText(itemNameSources.find(Boolean));
  if (!itemName) return null;

  const summaryParts = [`You purchased ${itemName}`];

  const recipientInfo = findRecipientInfo(order);
  if (recipientInfo?.label) {
    const { label, isRelation } = recipientInfo;
    summaryParts.push(
      isRelation ? `for your ${label}` : `for ${label}`
    );
  }

  const summary = summaryParts.join(' ') + '.';
  return summary;
};

// Assistant API — talks to govava-new-core (OpenAI Realtime + Anum stack).
// All requests require the customer bearer token stored in localStorage.userData.
const assistantBaseUrl = process.env.VUE_APP_URL || 'https://web-api.govava.com/';

const getCustomerToken = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return (
      userData?.user?.api_token ||
      userData?.user?.token ||
      userData?.customer?.api_token ||
      userData?.token ||
      userData?.api_token ||
      null
    );
  } catch (err) {
    console.warn('Failed to read customer token:', err);
    return null;
  }
};

const assistantClient = axios.create({
  baseURL: assistantBaseUrl,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

assistantClient.interceptors.request.use((config) => {
  const token = getCustomerToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Forward PostHog's client-side $session_id (and distinct_id) so server
  // captures land in the same Session Replay timeline as the browser events.
  try {
    if (typeof window !== 'undefined' && window.posthog) {
      const phSession = window.posthog.get_session_id && window.posthog.get_session_id();
      const phDistinct = window.posthog.get_distinct_id && window.posthog.get_distinct_id();
      if (phSession) config.headers['X-PostHog-Session-Id'] = phSession;
      if (phDistinct) config.headers['X-PostHog-Distinct-Id'] = phDistinct;
    }
  } catch (e) { /* ignore */ }
  return config;
});

export const assistantAPI = {
  async startSession() {
    const { data } = await assistantClient.post('customer/assistant/start');
    return data?.data ?? null;
  },

  async refreshSession(sessionId) {
    const { data } = await assistantClient.post(`customer/assistant/refresh/${encodeURIComponent(sessionId)}`);
    return data?.data ?? null;
  },

  async saveContext(sessionId, messages, { final = false, reason = null } = {}) {
    const body = { messages };
    if (final) body.final = true;
    if (reason) body.reason = reason;
    try {
      const { data } = await assistantClient.post(
        `customer/assistant/context/${encodeURIComponent(sessionId)}`,
        body
      );
      if (final) {
        storage.setLastConversationEndTime();
      }
      return data;
    } catch (err) {
      if (final) {
        storage.setLastConversationEndTime();
      }
      throw err;
    }
  },

  async getMemory() {
    const { data } = await assistantClient.get('customer/assistant/memory');
    return data?.data ?? null;
  },

  async upsertMemory(payload) {
    const { data } = await assistantClient.post('customer/assistant/memory', payload);
    return data?.data ?? null;
  },
};

export const purchaseAPI = {
  async getLatestOrderSummary(token) {
    if (!token) return null;

    try {
      const response = await axios.get(orderHistoryEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        params: {
          limit: 5,
        },
      });

      const orders = extractOrders(response?.data);
      if (!orders.length) {
        return null;
      }

      const latest =
        orders.find(
          (order) =>
            typeof order?.status === 'string' &&
            order.status.toLowerCase() === 'completed'
        ) ?? orders[0];

      return buildOrderSummary(latest);
    } catch (error) {
      console.error('Failed to fetch latest order summary:', error);
      return null;
    }
  },
};
